import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from 'ui-library';

export interface TemplateSaveDialogData {
  // Name of the template the draft was prefilled from, if any - when set, the dialog opens on
  // the update-vs-create choice; when absent (nothing was ever applied), it goes straight to
  // asking for a name, there being nothing to choose between.
  appliedTemplateName?: string;
}

export type TemplateSaveDialogResult = { mode: 'update' } | { mode: 'create'; name: string } | undefined;

// Shared by resources.component.ts, kyd-config-dialog.component.ts and kyd-bulk-config-dialog.
// component.ts's "Enregistrer comme modèle" action - the only place a name is still asked for
// once configuring a device stopped requiring one (see tracker-config.tool.ts). Deliberately
// distinct from the sensor-config save: this dialog only ever results in a TrackerConfig being
// created or updated, never touches the tracker itself.
@Component({
  selector: 'app-template-save-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatToolbar,
    ButtonComponent,
  ],
  standalone: true,
  templateUrl: './template-save-dialog.component.html',
  styleUrls: ['./template-save-dialog.component.scss']
})
export class TemplateSaveDialogComponent {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly data: TemplateSaveDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<TemplateSaveDialogComponent, TemplateSaveDialogResult> = inject(MatDialogRef<TemplateSaveDialogComponent, TemplateSaveDialogResult>);

  step: 'choice' | 'name' = this.data.appliedTemplateName ? 'choice' : 'name';
  name: string = '';
  nameError: string = '';

  get appliedTemplateName(): string | undefined {
    return this.data.appliedTemplateName;
  }

  chooseUpdate() {
    this.dialogRef.close({ mode: 'update' });
  }

  chooseCreate() {
    this.step = 'name';
  }

  backToChoice() {
    this.step = 'choice';
    this.nameError = '';
  }

  confirmName() {
    const trimmed = this.name?.trim();
    if (!trimmed) {
      this.nameError = this.translate.instant('units.device.config_name_required');
      return;
    }

    this.dialogRef.close({ mode: 'create', name: trimmed });
  }

  cancel() {
    this.dialogRef.close(undefined);
  }
}
